import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, exhaustMap, filter, finalize, map, mergeMap, tap } from 'rxjs/operators';
import { KrsEntry } from 'src/app/models/krs-entry/krs-entry.model';
import { KrsErrorModel } from 'src/app/models/krs-error.model';
import { EformsService } from 'src/app/services/eforms.service';
import { KrsEntryService } from 'src/app/services/krs-entry.service';

@Injectable({
  providedIn: 'root',
})
export class DanePodmiotuService implements OnDestroy {
  subscription: Subscription;
  private loading: Subject<boolean> = new Subject<boolean>();
  private searchEvent: Subject<string> = new Subject<string>();
  private errorMessage: BehaviorSubject<KrsErrorModel> = new BehaviorSubject<KrsErrorModel>(null);

  constructor(
    private eformsService: EformsService,
    private krsEntryService: KrsEntryService
  ) {
    this.initSearch();
  }

  ngOnDestroy(): void {
      this.subscription.unsubscribe();
  }

  public normalizeKRS(controlValue: string): string {
    return this.krsEntryService.normalizeKRS(controlValue);
  }

  getPodmiotObs(): Observable<KrsEntry> {
    return this.krsEntryService.krsEntry$;
  }

  getLoadingObs(): Observable<boolean> {
    return this.loading.asObservable();
  }

  getErrorMessageObs(): Observable<KrsErrorModel> {
    return this.errorMessage.asObservable();
  }

  search(value: string): void {
    this.searchEvent.next(value);
  }

  clearPodmiot() {
    if(this.krsEntryService.getKrsEntry()) {
      this.krsEntryService.setKrsEntry(null);
    }
    if(this.errorMessage.value) {
      this.errorMessage.next(null);
    }
  }

  private initSearch(): void {
    this.subscription = this.searchEvent
      .pipe(
        tap(() => this.loading.next(true)),
        tap(() => this.errorMessage.next(null)),
        map((controlValue) => this.normalizeKRS(controlValue)),
        tap(mappedKrsValue => this.handleErrorOnInvalidKrsNumber(mappedKrsValue)),
        filter(mappedKrsValue => mappedKrsValue !== null),
        exhaustMap(krs => this.eformsService.getKrs(krs)
          .pipe(
            catchError(error => this.handleHttpError(error)),
            finalize(() => this.loading.next(false))
          )
        ),
        tap(value => this.checkErrors(value)),
        mergeMap((krsEntry) => this.krsEntryService.jestWykreslonyZKrs(krsEntry) ? of(null) : of(krsEntry)),
        tap(value => this.krsEntryService.setKrsEntry(value)),
      )
      .subscribe()
  }

  private handleErrorOnInvalidKrsNumber(value: string): void {
    if (!value) {
      this.loading.next(false);
      this.updateStateOnError({ heading: 'niepoprawnyNumerKrs', note: 'niepoprawnyNumerKrsNote' });
    }
  }

  private updateStateOnError(message: KrsErrorModel): void {
    this.krsEntryService.setKrsEntry(null);
    this.errorMessage.next(message);
  }

  private handleHttpError(error: HttpErrorResponse): Observable<never> {
    this.updateStateOnError({ heading: 'bladTechnicznyWZapytaniu', note: 'bladTechnicznyWZapytaniuNote' });
    return EMPTY;
  }

  private checkErrors(krsEntry: KrsEntry): void {
    if (krsEntry === null) {
      this.updateStateOnError({ heading: 'podmiotNieZostalOdnalezionyWKrs', note: 'podmiotNieZostalOdnalezionyWKrsNote' });
      return;
    }
    if (this.krsEntryService.jestWykreslonyZKrs(krsEntry)) {
      this.updateStateOnError({ heading: 'podmiotWykreslonyZKrs', note: 'podmiotWykreslonyZKrsNote' });
      return;
    }
  }
}
